import { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from "react";
import { useApi } from "@/shared/utils/request";
import { logger } from "@/shared/utils/logger";
import { format, toZonedTime } from "date-fns-tz";
import { io } from "socket.io-client";
import { useAuth } from "@workos-inc/authkit-react";
import { toast } from "sonner";
import { TOAST_DURATION } from "@/shared/utils/toast";

export type Entry = {
  id: number;
  date: string;
  matter: string;
  matter_id?: string;
  matter_name?: string;
  client?: string;
  description: string;
  task: string;
  activity?: string;
  document_name?: string;
  duration: number;
  units_seconds?: number;
  start_time?: string;
  end_time?: string;
  application?: string;
  // Admin feedback fields
  approved?: boolean;
  needs_improvement?: boolean;
  attorney_feedback?: string | null;
};

type EntriesContextType = {
  // Data
  entries: Entry[];
  exportedEntries: Entry[];
  deletedEntries: Entry[];

  // Loading states
  isLoading: boolean;
  isProcessingEntry: boolean;
  processingApplication: string | null;

  // Refresh functions
  refreshRecords: () => Promise<void>;

  // Optimistic updates
  updateEntries: (updater: (prev: Entry[]) => Entry[]) => () => void;
  mutatingIds: Set<number>;
  setMutatingIds: React.Dispatch<React.SetStateAction<Set<number>>>;
};

const EntriesContext = createContext<EntriesContextType | undefined>(undefined);

export function EntriesProvider({ children }: { children: ReactNode }) {
  const { makeApiRequest } = useApi();
  const { user, isLoading: isAuthLoading } = useAuth();

  const [entries, setEntries] = useState<Entry[]>([]);
  const [exportedEntries, setExportedEntries] = useState<Entry[]>([]);
  const [deletedEntries, setDeletedEntries] = useState<Entry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessingEntry, setIsProcessingEntry] = useState(false);
  const [processingApplication, setProcessingApplication] = useState<string | null>(null);
  const [mutatingIds, setMutatingIds] = useState<Set<number>>(new Set());
  const lastConnectErrorToast = useRef<number>(0);

  // Ref so WebSocket handlers always read the current user (effect runs once with [])
  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);

  const updateEntries = useCallback((updater: (prev: Entry[]) => Entry[]) => {
    let snapshot: Entry[];
    setEntries(prev => { snapshot = prev; return updater(prev); });
    return () => setEntries(snapshot!);
  }, []);

  function transformData(data: any[]): Entry[] {
    const formatInTimeZone = (parsedTime: Date, fmt: string, tz: string) =>
      format(toZonedTime(parsedTime, tz), fmt, { timeZone: tz });

    return (data || [])
      .map((record: any) => ({
        id: record.id,
        date: record.date
          ? formatInTimeZone(new Date(record.date), "MM/dd/yyyy", "UTC")
          : "",
        matter: record.matter || record.matter_name || "",
        matter_id: record.matter_id || "",
        matter_name: record.matter_name || "",
        client: record.client || "",
        description: record.description || "",
        task: record.task || "",
        activity: record.activity || "",
        document_name: record.document_name || "",
        duration: parseFloat(record.duration || record.total_hours) || 0,
        units_seconds: record.units_seconds || 0,
        start_time: record.start_time || "",
        end_time: record.end_time || "",
        application: record.application || "",
        // Admin feedback fields
        approved: record.approved,
        needs_improvement: record.needs_improvement,
        attorney_feedback: record.attorney_feedback,
      }))
      .sort((a, b) => {
        if (a.end_time && b.end_time) {
          return new Date(b.end_time).getTime() - new Date(a.end_time).getTime();
        }
        return 0;
      });
  }

  const refreshRecords = useCallback(async () => {
    try {
      const startTime = performance.now();

      const [recordsResponse, exportedResponse, deletedResponse] = await Promise.all([
        makeApiRequest({ endpoint: "/records", method: "GET" }),
        makeApiRequest({ endpoint: "/records/exported", method: "GET" }),
        makeApiRequest({ endpoint: "/records/deleted", method: "GET" }),
      ]);

      const transformedRecords = transformData(recordsResponse.data || []);
      const transformedExported = transformData(exportedResponse.data || []);
      const transformedDeleted = transformData(deletedResponse.data || []);

      setEntries(transformedRecords);
      setExportedEntries(transformedExported);
      setDeletedEntries(transformedDeleted);

      logger.debug("Records loaded", {
        recordCount: transformedRecords.length,
        exportedCount: transformedExported.length,
        deletedCount: transformedDeleted.length,
        durationMs: Math.round(performance.now() - startTime),
      });
    } catch (e) {
      if (e instanceof Error) {
        logger.captureException(e, { action: 'refreshRecords' });
      }
      toast.error("Failed to load entries", {
        description: e instanceof Error ? e.message : undefined,
        duration: TOAST_DURATION.error,
      });
    }
  }, [makeApiRequest]);

  // Initial data load - wait for authentication to complete
  useEffect(() => {
    if (!isAuthLoading && user) {
      setIsLoading(true);
      refreshRecords().finally(() => setIsLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthLoading, user]);

  // WebSocket connection for real-time entry processing updates
  useEffect(() => {
    // set up websocket connection
    const subdomain = window.location.hostname.split(".")[0];
    let API_URL_BASE = `https://${subdomain}api.hourglass.law`;

    if (window.location.hostname.includes("bpmlaw")) {
      API_URL_BASE = `https://hourglassapi.bpmlaw.com`;
    } else if (window.location.hostname.includes("tysonmendes")) {
      API_URL_BASE = `https://hourglassapi.tysonmendes.com`;
    } else if (window.location.hostname.includes("pregodonnell")) {
      API_URL_BASE = `https://hourglassapi.pregodonnell.com`;
    } else if (window.location.hostname.includes("localhost")) {
      API_URL_BASE = `http://localhost:8000`;
    } else if (window.location.hostname.includes("app.hourglass.law")) {
      API_URL_BASE = `https://api.hourglass.law`;
    }
    const socket = io(API_URL_BASE, { transports: ["websocket"] });

    const loadRecords = async () => {
      try {
        await refreshRecords();
      } catch (error) {
        if (error instanceof Error) {
          logger.captureException(error, { action: 'loadRecordsWebSocket' });
        }
      }
    };

    socket.on("connect", () => {
      logger.debug("WebSocket connected");
    });

    socket.on("entry processing started", (data) => {
      if (data.email === userRef.current?.email) {
        setIsProcessingEntry(true);
        setProcessingApplication(data.application || null);
      }
    });

    socket.on("entry processing completed", (data) => {
      if (data.email === userRef.current?.email) {
        setIsProcessingEntry(false);
        setProcessingApplication(null);
        loadRecords();
      }
    });

    // Listen for admin feedback updates on attorney entries
    socket.on("entry_feedback_updated", (data) => {
      if (data.email === userRef.current?.email) {
        loadRecords();
      }
    });

    socket.on("connect_error", (error) => {
      logger.captureException(error, { action: 'websocketConnect' });
      const now = Date.now();
      if (now - lastConnectErrorToast.current > 30_000) {
        lastConnectErrorToast.current = now;
        toast.error("Lost connection to server", { duration: TOAST_DURATION.error });
      }
    });

    return () => {
      socket.off("connect");
      socket.off("entry processing started");
      socket.off("entry processing completed");
      socket.off("entry_feedback_updated");
      socket.off("connect_error");
      socket.disconnect();
    };
  }, []);

  const value: EntriesContextType = {
    entries,
    exportedEntries,
    deletedEntries,
    isLoading,
    isProcessingEntry,
    processingApplication,
    refreshRecords,
    updateEntries,
    mutatingIds,
    setMutatingIds,
  };

  return (
    <EntriesContext.Provider value={value}>
      {children}
    </EntriesContext.Provider>
  );
}

export function useEntries() {
  const context = useContext(EntriesContext);
  if (context === undefined) {
    throw new Error("useEntries must be used within an EntriesProvider");
  }
  return context;
}
